import {
  useMutation,
  useQuery,
  type UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import axios, { AxiosRequestConfig } from 'axios';
import { useEffect } from 'react';

const getRequest = <TResult = any, TError = any>(
  path: string,
  name?: string | string[],
  options?: Omit<UseQueryOptions<TResult, TError>, 'queryKey' | 'queryFn'> & {
    onSuccess?: (data: TResult) => void;
    onError?: (error: TError) => void;
  },
  axiosReqConfig?: AxiosRequestConfig
): UseQueryResult<TResult, TError> => {
  const { onSuccess, onError, ...queryOptions } = options || {};

  const query = useQuery<TResult, TError>({
    queryKey: Array.isArray(name) ? name : [name ?? path],
    queryFn: async () => {
      try {
        const config = axiosReqConfig
          ? {
              ...axiosReqConfig,
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...axiosReqConfig.headers,
              },
            }
          : {
              headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
            };
        const response = await axios.get<TResult>(path, config);

        return response.data;
      } catch (error) {
        if(error instanceof Error) {
          throw new Error(error.message);
        }
        else throw new Error(`Failed to fetch ${name ?? path}`)
      }
    },
    refetchOnWindowFocus: false,
    ...options,
  });

  useEffect(() => {
    if (query && query.isSuccess && onSuccess) {
      onSuccess(query.data);
    }
  }, [query.data, query.isSuccess]);

  useEffect(() => {
    if (query.isError && query.error && onError) {
      onError(query.error);
    }
  }, [query.isError, query.error]);

  return query;
};

const postRequest = <TResult>(path: string) =>
  useMutation<any, Error, BodyInit>({
    mutationFn: (body: BodyInit) => {
      return axios
        .post<any>(path, body, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        .catch((responseObj) => {
          const detail = responseObj?.response?.data?.detail;
          if (typeof detail === 'string' && detail.length > 0) {
            throw new Error(detail);
          }
          throw new Error(
            responseObj?.response?.statusText ||
              responseObj?.message ||
              'Request failed'
          );
        })
        .then(({ data }) => {
          if (data?.error && data?.error.length > 0) {
            throw new Error(data.error);
          }
          return data;
        });
    },
  });

export { getRequest, postRequest };
